import { getOrCreateMainLogger } from "../logger/main";
import { mainBus } from "./MainBus";
import { isDevLaunch } from "../util";
import path from "path";
import { App } from "electron";

const logger = getOrCreateMainLogger();

export class ProtocolHandler {
	protocolURL?: URL;

	app: App;

	constructor(app: App) {
		this.app = app;
	}

	/**
	 * Checks if the string passed is a url, and if so saves it as the current protocolURL.
	 */
	saveIfProtocolString(s: string) {
		try {
			this.protocolURL = new URL(s);
		} catch (e) {}
	}

	/**
	 * Returns true if the protocol used was a custom protocol
	 */
	isCustom(): boolean {
		return this.protocolURL?.hostname.toLowerCase() === "custom";
	}

	/**
	 * Saves the protocol URL if this instance was launched via protocol handler and
	 * registers the fsbl:// protocol handler. Each time FEA is started, the existing protocol
	 * handler is removed and replaced. This ensures that the latest FEA is being called whenever
	 * it is upgraded or if multiple instances are installed.
	 */
	handleFirstInstance() {
		logger.log(`Protocol client data received for startup ${process.argv}`);
		// The protocol handler URL will always the last parameter in the command line when
		// an app is launched via protocol handler on MS Windows
		this.saveIfProtocolString(process.argv[process.argv.length - 1]);
		this.registerProtocolHandler();
	}

	/**
	 * This is called when a protocol handler is called for an already running FEA. Electron interprets
	 * this as a "second instance". We parse the protocol and send the message up to Finsemble so that
	 * it can navigate or react as appropriate.
	 */
	handleSecondInstance(commandLineParameter: string) {
		this.saveIfProtocolString(commandLineParameter);
		if (this.protocolURL && this.isCustom()) {
			mainBus.sendEvent("systemEvent.protocol-handler-triggered", {
				url: this.protocolURL.toString(),
			});
		}
	}

	/**
	 * Registers a protocol handler with the OS.
	 *
	 * Note that app.removeAsDefaultProtocolClient no longer seems to work, but this code
	 * still overwrites any previous entry.
	 */
	private registerProtocolHandler() {
		const protocolPrefix = "fsbl";

		if (isDevLaunch()) {
			/**
			 * If we are running a non-packaged (dev) version of the app && on windows then we set the protocol handler
			 * to manually launch "<feapath>/node_modules/.../electron.exe <feapath>/dist/app.js"
			 *
			 * This then allows a developer to launch Finsemble using protocol handling methodology:
			 * fsbl://manifest-url/http%3A%2F%2Ffinsemble.com%3A3375%2Fconfigs%2Fapplication%2Fmanifest-local-dev.json
			 */
			if (process.platform === "win32") {
				// Set the path of electron.exe and your app. App path needs to be quoted to work with spaces.
				// These two additional parameters are only available on windows.

				// This assumes an argument ending in `.js` is our electron main entry file.
				const appPaths = process.argv.filter((arg) => arg.endsWith(".js"));

				// If 0 js file arguments are passed, log an error
				if (appPaths.length == 0) {
					logger.error(
						`Registered development protocol handler FAILED:  protocolPrefix:${protocolPrefix}; process.execPath: ${process.execPath}; allArgs:${process.argv}`
					);
				} else {
					// If 2 or more js file arguments are passed, log an error, but use the first
					if (appPaths.length > 1) {
						logger.error(`Registered development protocol handler found multiple js files!!! Using first.`);
					}
					// The e2e testing suite passes `--app=path/to/dist.js`, so we use a regex replacement to remove `--app=`.
					// Additionally, the e2e / selenium suite needs remote-debugging to function, so always add remote-debugging-port
					const appArgs = [path.resolve(appPaths[0].replace(/.*=/, "")), "--remote-debugging-port=9222"];
					this.app.setAsDefaultProtocolClient(protocolPrefix, process.execPath, appArgs);
					logger.debug(
						`Registered development protocol handler:  protocolPrefix:${protocolPrefix}; process.execPath: ${process.execPath}; args:["${appPaths[0]}"]}; allArgs:${process.argv}`
					);
				}
			}
		} else {
			this.app.setAsDefaultProtocolClient(protocolPrefix);
			logger.debug(`Registered protocol handler ${protocolPrefix}`);
		}
	}
}
